import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Search, UserRound } from "lucide-react";

import { api } from "@/lib/api";
import { Header } from "@/components/header";
import { Badge, Empty, ErrorView, Loading, SearchInput } from "@/components/kit";
import { useDebounced } from "@/pages/admin/referrals";
import { date, eur, PROJECT_LABELS, STATUS_LABELS, statusColor } from "@/lib/format";

function ClientCard({ c }) {
  return (
    <div data-testid={`client-item-${c.key}`} className="flex flex-col gap-2 rounded-[20px] border border-[#333333] bg-[#1E1E1E] p-4">
      <div className="flex items-center gap-2">
        <h3 className="t-h3 min-w-0 flex-1 truncate">{c.name}</h3>
        {c.signed_amount > 0 && <span className="t-h3 !text-[#27AE60]">{eur(c.signed_amount)}</span>}
      </div>
      <p className="t-caption">{[c.address, `${c.postal_code} ${c.city}`].filter(Boolean).join(", ")}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {c.phone ? (
          <a href={`tel:${c.phone}`} data-testid={`client-call-${c.key}`} className="focus-ring flex min-h-8 items-center gap-1.5 rounded text-sm text-[#D4AF37] hover:underline">
            <Phone className="h-4 w-4" aria-hidden /> {c.phone}
          </a>
        ) : null}
        {c.email ? (
          <a href={`mailto:${c.email}`} data-testid={`client-mail-${c.key}`} className="focus-ring flex min-h-8 min-w-0 items-center gap-1.5 rounded text-sm text-[#D4AF37] hover:underline">
            <Mail className="h-4 w-4 shrink-0" aria-hidden /> <span className="truncate">{c.email}</span>
          </a>
        ) : null}
      </div>
      {c.dossiers.map((d) => (
        <Link key={d.id} to={`/admin/dossier/${d.id}`} data-testid={`client-dossier-${d.number}`} className="focus-ring flex flex-col gap-1 border-l-2 py-2 pl-3 transition-colors hover:bg-white/5" style={{ borderLeftColor: statusColor(d.status) }}>
          <span className="flex justify-between">
            <span className="t-caption !text-[#D4AF37]">{d.number} · {PROJECT_LABELS[d.project_type]}</span>
            <span className="t-caption">{date(d.created_at)}</span>
          </span>
          <span className="t-caption">Agent : {d.agent_name} · Maître d'œuvre : {d.master?.company_name ?? "à attribuer"}</span>
          <Badge label={STATUS_LABELS[d.status]} color={statusColor(d.status)} />
        </Link>
      ))}
    </div>
  );
}

export default function AdminClients() {
  const [search, setSearch] = useState("");
  const term = useDebounced(search.trim());
  const q = useQuery({
    queryKey: ["admin-clients", term],
    queryFn: () => api(`/admin/clients?q=${encodeURIComponent(term)}`),
    placeholderData: (prev) => prev,
  });
  return (
    <div data-testid="admin-clients-screen" className="flex flex-col">
      <Header title="Clients" subtitle="Tous les clients référencés" />
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 p-4 pb-8 md:p-8">
        <div className="max-w-xl">
          <SearchInput icon={Search} testID="admin-clients-search-input" value={search} onChange={setSearch} placeholder="Nom, téléphone, e-mail, ville, n° dossier…" />
        </div>
        {q.isLoading ? <Loading /> : q.isError || !q.data ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
          <>
            <p className="t-caption" data-testid="admin-clients-count">{q.data.total} client(s)</p>
            {q.data.items.length ? (
              <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">{q.data.items.map((c) => <ClientCard key={c.key} c={c} />)}</div>
            ) : (
              <Empty icon={UserRound} title={term ? "Aucun résultat" : "Aucun client"} text={term ? `Aucun client ne correspond à « ${term} ».` : "Chaque client recommandé par un agent est référencé ici automatiquement."} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
